import type { ObligationContract, PaymentAllocationContract } from "../../contracts/src/index.js";
import { wholeMad } from "../../domain/academics-obligations/src/index.js";

export interface PayablePeriod {
  readonly period: string;
  readonly obligations: readonly ObligationContract[];
  readonly remainingMad: string;
  readonly settledMad: string;
  readonly overdue: boolean;
}

export function payablePeriods(
  obligations: readonly ObligationContract[],
): readonly PayablePeriod[] {
  const groups = new Map<string, ObligationContract[]>();
  for (const obligation of obligations) {
    const current = groups.get(obligation.period) ?? [];
    current.push(obligation);
    groups.set(obligation.period, current);
  }
  return [...groups.entries()]
    .map(([period, periodObligations]) => ({
      period,
      obligations: periodObligations,
      remainingMad: periodObligations
        .reduce((total, obligation) => total + wholeMad(obligation.remainingMad), 0n)
        .toString(),
      settledMad: periodObligations
        .reduce((total, obligation) => total + wholeMad(obligation.settledMad), 0n)
        .toString(),
      overdue: periodObligations.some((obligation) => obligation.overdue),
    }))
    .sort((left, right) => {
      const leftRegistration = left.obligations.some(
        (obligation) => obligation.family === "registration",
      );
      const rightRegistration = right.obligations.some(
        (obligation) => obligation.family === "registration",
      );
      if (leftRegistration !== rightRegistration) return leftRegistration ? -1 : 1;
      return left.period.localeCompare(right.period);
    });
}

export function allocatePayablePeriods(
  obligations: readonly ObligationContract[],
  periods: ReadonlySet<string>,
): readonly PaymentAllocationContract[] {
  return obligations
    .filter(
      (obligation) => periods.has(obligation.period) && wholeMad(obligation.remainingMad) > 0n,
    )
    .sort((left, right) => {
      if (left.overdue !== right.overdue) return left.overdue ? -1 : 1;
      return left.dueDate.localeCompare(right.dueDate) || left.id.localeCompare(right.id);
    })
    .map((obligation) => ({
      obligationId: obligation.id,
      amountMad: obligation.remainingMad,
    }));
}

export function proposePaymentAllocations(
  obligations: readonly ObligationContract[],
  amountMad: string,
): readonly PaymentAllocationContract[] {
  let remaining = wholeMad(amountMad);
  if (remaining <= 0n) throw new Error("Payment amount must be positive");
  const ordered = [...obligations].sort((left, right) => {
    if (left.overdue !== right.overdue) return left.overdue ? -1 : 1;
    return left.dueDate.localeCompare(right.dueDate) || left.id.localeCompare(right.id);
  });
  const result: PaymentAllocationContract[] = [];
  for (const obligation of ordered) {
    if (remaining === 0n) break;
    const capacity = wholeMad(obligation.remainingMad);
    const allocation = capacity < remaining ? capacity : remaining;
    if (allocation > 0n) {
      result.push({ obligationId: obligation.id, amountMad: allocation.toString() });
      remaining = wholeMad((remaining - allocation).toString());
    }
  }
  if (remaining > 0n) throw new Error("Payment exceeds outstanding obligations");
  return result;
}

export function allocationTotal(allocations: readonly PaymentAllocationContract[]): string {
  return allocations
    .reduce((total, allocation) => total + wholeMad(allocation.amountMad), 0n)
    .toString();
}
